import type { Travelers } from "@b2bportal/packages-api";

export interface PackageFlightPassengerCounts {
  ADT: number;
  CNN: number;
  INF: number;
  INS: number;
}

const MIN_ADULT_AGE = 12;
const MIN_CHILD_AGE = 2;

/**
 * Maps package search guests to flight passenger types using airline age
 * bands (adult 12+, child 2–11, infant under 2).
 *
 * Child ages are usually 2–11 for flight packages, but can be 0–17 when a
 * hotel + car search is switched to a flight package or arrives via deep link.
 * Out-of-band ages are classified by age rather than all being sent as CNN;
 * an under-2 child age is treated as an infant in seat since it occupies its
 * own place in the search.
 */
export const getPackageFlightPassengerCounts = (
  guests: Travelers
): PackageFlightPassengerCounts =>
  guests.childAges.reduce<PackageFlightPassengerCounts>(
    (counts, age) => {
      if (age >= MIN_ADULT_AGE) return { ...counts, ADT: counts.ADT + 1 };
      if (age >= MIN_CHILD_AGE) return { ...counts, CNN: counts.CNN + 1 };
      return { ...counts, INS: counts.INS + 1 };
    },
    {
      ADT: guests.adultCount,
      CNN: 0,
      INF: guests.infantOnLapAges.length,
      INS: guests.infantInSeatAges.length,
    }
  );

/**
 * Flight shop `passengers` payload for a package search, omitting zero counts.
 */
export const buildPackageFlightShopPassengers = (
  guests: Travelers
): Partial<PackageFlightPassengerCounts> => {
  const { ADT, CNN, INF, INS } = getPackageFlightPassengerCounts(guests);
  return {
    ADT,
    ...(CNN ? { CNN } : {}),
    ...(INF ? { INF } : {}),
    ...(INS ? { INS } : {}),
  };
};
